// Status and sign-off date: ONE definition of what each is allowed to
// become, used by the row controls and by the bulk setter alike.
//
// Owner, 2026-09-29: "then allow per row edit and bulk edit" - BOTH, FOR
// BOTH FIELDS. The leader's condition on it is the reason this file exists
// rather than two branches in the page: "bulk cannot set anything the
// per-row editor would refuse", and "a bulk setter that produces rows the
// per-row editor could never have made is the thing we are trying not to
// build".
//
// THE DERIVED BEHAVIOUR IS THE POINT. Neither field is a plain assignment:
//   - status `done` also completes the row - progress goes to 100, and the
//     server closes the issues and checklist items hanging off it. Coming
//     back OFF done has to undo the 100, or a reopened activity claims to
//     be finished.
//   - a sign-off date cannot be edited once the row is SIGNED. The date is
//     what was signed; changing it under a recorded sign-off would rewrite
//     history, and the row editor has always refused it.
//   - a DISABLED row takes neither. It was called off; its issues and
//     checklist items are already abandoned.
//
// A patch of `null` means SKIP THIS ROW - not "no change wanted" but "this
// row cannot take it". The caller counts those and says so, because a bulk
// edit that silently passes over a third of the selection is the silent
// degradation we keep finding.

/** The four values the field actually has, with what a person calls them. */
export const STATUS_OPTIONS = [
  { value: "planned", label: "Planned" },
  { value: "in_progress", label: "In progress" },
  { value: "done", label: "Done" },
  { value: "on_hold", label: "On hold" },
];

export const STATUS_VALUES = STATUS_OPTIONS.map((o) => o.value);

const isSigned = (r) => !!(r && r.signedOffAt);

/**
 * What setting `next` does to one row. `null` = the row cannot take it.
 *
 * This is the tickbox's behaviour generalised, not a new rule: the tickbox
 * wrote done/in_progress with progress 100/99, and every other value now
 * goes through the same place.
 */
export function statusPatch(row, next) {
  if (!row || row.disabled) return null;
  if (!STATUS_VALUES.includes(next)) return null;
  if ((row.status || "planned") === next) return null;
  const patch = { status: next };
  if (next === "done") {
    patch.progress = 100;
  } else if (row.status === "done" || row.progress === 100) {
    // Leaving done: 100% would say the work is finished when it is not.
    // 99 rather than 0 - the tickbox's own choice, kept, because the
    // progress somebody reported is not wiped by reopening the row.
    patch.progress = 99;
  }
  return patch;
}

/**
 * What setting a sign-off date to `iso` ("" clears it) does to one row.
 * `null` = the row cannot take it: signed rows and disabled rows.
 */
export function signOffPatch(row, iso) {
  if (!row || row.disabled) return null;
  if (isSigned(row)) return null;
  const v = String(iso || "").trim();
  if ((row.signOffDate || "") === v) return null;
  // The FIRST planned date is kept once the date moves - the grey star on
  // the Gantt (owner, 2026-10-10). Moving back to it, or clearing the date,
  // drops it. The server applies the same rule on save
  // (back/utils/signOffOriginal), so this is the draft showing it early.
  const original = row.signOffDateOriginal || row.signOffDate || "";
  return { signOffDate: v, signOffDateOriginal: v && original && original !== v ? original : "" };
}

/**
 * Apply one of the two over a selection. Returns the rows to change and
 * WHY each of the others was left out, so the page can say so rather than
 * quietly doing less than it was asked.
 */
export function planBulk(rows, keys, patchOf) {
  const set = keys instanceof Set ? keys : new Set(keys || []);
  const changes = [];
  const skipped = { disabled: 0, signed: 0, unchanged: 0 };
  (rows || []).forEach((r) => {
    if (!set.has(r.key)) return;
    const patch = patchOf(r);
    if (patch) { changes.push({ key: r.key, patch }); return; }
    if (r.disabled) skipped.disabled += 1;
    else if (isSigned(r)) skipped.signed += 1;
    else skipped.unchanged += 1;
  });
  return { changes, skipped };
}

/**
 * TICKING A PARENT TICKS EVERYTHING UNDER IT, RECURSIVELY.
 *
 * Owner, 2026-10-01: "why is the check box on the schedule doesn't also
 * automatically cehckbox all children recursively".
 *
 * IT WAS ALREADY PROMISED AND NOT DONE. The Done column's own tooltip has
 * said "(child activities follow)" and "Untick to put them all back" since it
 * was built, and the handler called statusPatch for ONE row. A parent went
 * green with its children left as they were - so the schedule said a group
 * was finished while the work inside it was not, which is the one thing a
 * summary row must never be able to say.
 *
 * WHOLE SUBTREE, NOT DIRECT CHILDREN. He said recursively and the data agrees:
 * a level holds areas which hold activities, so stopping at one generation
 * would leave the grandchildren open under a done parent - the same lie one
 * level deeper.
 *
 * UNTICKING CASCADES TOO, because the tooltip promises it and because the
 * asymmetry would be worse: tick to finish sixty rows, untick and reopen one.
 *
 * WHAT IS SKIPPED AND WHY IT IS RIGHT TO SKIP IT - planBulk's own rules, not
 * new ones here: a DISABLED row is deliberately out of the schedule and is
 * left alone, and a row already in the target state produces no patch. So the
 * count this returns is what CHANGED, not what was walked.
 */
export function statusCascade(rows, key, next, { subtreeKeys }) {
  if (!rows || !key || typeof subtreeKeys !== 'function') return { changes: [], skipped: { disabled: 0, signed: 0, unchanged: 0 } };
  // The subtree INCLUDES the row itself - the parent is being ticked too.
  return planBulk(rows, subtreeKeys(rows, key), (r) => statusPatch(r, next));
}

/** One sentence for what a bulk run actually did. */
export function describeBulk({ changes, skipped }) {
  const parts = [`${changes.length} row${changes.length === 1 ? "" : "s"} changed`];
  if (skipped.signed) parts.push(`${skipped.signed} already signed off`);
  if (skipped.disabled) parts.push(`${skipped.disabled} disabled`);
  if (skipped.unchanged) parts.push(`${skipped.unchanged} already had it`);
  return parts.join(" · ");
}
